import React, { Component } from "react";
import PropTypes from "prop-types";
import { Select } from "@terraincognita/ui-core";
import { RaisedButton } from "material-ui";
import { PublishStatusBanner } from "./PublishStatusBanner";

const PUBLISH_POLL_INTERVAL = 5000;
const COMPLETE_BANNER_DURATION = 15 * 1000;
/*
Watchdog window for the publish POST. If the API has not produced a
new publishHistory entry within this window after the user clicks
Publish, we treat the publish as having failed silently and surface
an error banner. The server-side stale-publish guard
(STALE_PUBLISH_TIMEOUT_MS = 5 minutes in mod-api) only fires when an
entry exists to advance, so it cannot rescue a publish that never
wrote a "preparing" entry -- this client-side watchdog covers that
gap. 30s is comfortably longer than a healthy prepKioskPublishStatus-
PreparingData round-trip (typically sub-second) while still surfacing
a clear failure to the user before they wait indefinitely.
*/
const PUBLISH_WATCHDOG_TIMEOUT = 30 * 1000;

export class PublishManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      publishInitiated: false,
      publishCompletedAt: null,
      completeBannerDismissed: false,
      errorDismissed: false,
      /*
      Snapshot of every publishId present in publishHistory at the
      moment the user clicked Publish. Used to identify the new
      publish's first entry once polling picks it up: the new entry
      is the (only) one whose publishId is not in this set. Reset on
      each click. Null when no publish session is in flight from this
      component instance.
      */
      knownPublishIdsAtClick: null,
      /*
      Soft (session-scoped) error message produced by either the
      saga's failure path or the watchdog firing. Distinct from
      isLastPublishError(), which reads metadata.error on the last
      publishHistory entry; soft errors apply when no entry exists.
      */
      softErrorMessage: null,
      /*
      The publishErrorAt value from kioskData that we have already
      reacted to, so a stale error doesn't fire repeatedly across
      renders. Only acted on while publishInitiated is true.
      */
      lastSeenPublishErrorAt: null
    };
    this._publishStatusInterval = null;
    this._completeBannerTimeout = null;
    this._watchdogTimeout = null;
    /*
    Last value reported to the parent via onPublishingStateChange.
    Tracked on the instance (not in state) to avoid an unnecessary
    re-render whenever the derived value is recomputed -- the only
    purpose is to compare against the latest derived value and call
    the callback exactly when the value flips.
    */
    this._lastReportedIsPublishing = null;
    this.handleProjectChange = this.handleProjectChange.bind(this);
    this.handlePublishClick = this.handlePublishClick.bind(this);
    this.handleCloseBanner = this.handleCloseBanner.bind(this);
    this.handleDismissError = this.handleDismissError.bind(this);
    this.handleDismissSoftError = this.handleDismissSoftError.bind(this);
    this.handleBannerViewDetails = this.handleBannerViewDetails.bind(this);
  }

  componentDidMount() {
    if (this.isPublishingInProgress()) {
      this.startPublishPolling();
    } else {
      this.checkForRecentCompletion();
    }
    /*
    Report initial value so the parent (KioskManager) can synchronize
    sibling components (PublishHistory uses this to disable the
    Restore action while a publish is in flight). Subsequent changes
    are reported from componentDidUpdate.
    */
    this.reportPublishingStateChange();
  }

  componentDidUpdate(prevProps) {
    const wasInProgress = this.isPublishingInProgressFromData(prevProps.kioskData);
    const isInProgress = this.isPublishingInProgress();

    /*
    Once the new publish's first entry shows up in publishHistory we
    no longer need the optimistic publishInitiated flag -- the banner
    can be driven by the canonical isPublishingInProgress signal, and
    the View Details link has a real publishId to point at. The
    snapshot is also cleared since its job is done.
    */
    const newPublishEntry = this.getNewPublishEntry();
    if (this.state.publishInitiated && newPublishEntry) {
      this.clearWatchdog();
      this.setState({
        publishInitiated: false,
        knownPublishIdsAtClick: null
      });
      /*
      Fast-publish race: if the entire publish lifecycle (CMS build
      + echo notify + kiosk download + install + complete) fits
      inside a single poll interval, the poll that first observes
      the new publish will already carry its terminal "complete"
      status. In that case the client never observed a non-terminal
      publishStatus for this publishId, so the
      wasInProgress/isInProgress comparison below cannot detect the
      transition and the complete banner would silently be skipped.
      Surface it here instead when the most recent entry for the
      new publish is already "complete". Gated on
      !isLastPublishCancelled to match the transition-path behavior;
      an errored publish wouldn't have status "complete" on its
      latest entry, so no isLastPublishError check is needed. This
      path is particularly relevant for collection bundles, which
      are small enough that a full publish commonly finishes inside
      the 5s poll window.
      */
      if (
        newPublishEntry.status === "complete" &&
        !this.isLastPublishCancelled()
      ) {
        this.showCompleteBanner();
      }
    }

    /*
    Saga-driven error path: the publish POST itself failed (network,
    auth, validation, etc.), so no history entry will ever appear.
    The kiosks reducer stamps publishErrorAt onto the kiosk slice
    when KIOSK_PUBLISH_ERROR fires. Detect a new value here while a
    publish is in flight and surface it as a soft error.
    */
    const currentErrorAt = this.props.kioskData?.publishErrorAt || null;
    if (
      this.state.publishInitiated &&
      currentErrorAt &&
      currentErrorAt !== this.state.lastSeenPublishErrorAt
    ) {
      this.handleSoftFailure(
        this.props.kioskData?.publishErrorMessage ||
          "The publish request failed. Please try again."
      );
      this.setState({ lastSeenPublishErrorAt: currentErrorAt });
    }

    if (wasInProgress && !isInProgress && !this.isLastPublishCancelled()) {
      this.showCompleteBanner();
    }

    if (!prevProps.kioskData && this.props.kioskData && !isInProgress && !this.state.publishCompletedAt) {
      this.checkForRecentCompletion();
    }

    if (!this._publishStatusInterval && isInProgress) {
      this.startPublishPolling();
    } else if (this._publishStatusInterval && !isInProgress && wasInProgress) {
      this.stopPublishPolling();
    }

    /*
    Notify parent if the derived "is a publish currently in flight?"
    value has changed since the last report. setState calls earlier
    in this method are async and won't be reflected in this.state
    yet, but they trigger another render -> componentDidUpdate cycle
    where the new state is observed and reported. So reporting at
    the end of every cycle eventually surfaces every transition.
    */
    this.reportPublishingStateChange();
  }

  componentWillUnmount() {
    this.stopPublishPolling();
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
      this._completeBannerTimeout = null;
    }
    this.clearWatchdog();
  }

  /*
  Single source of truth for "is a publish currently in flight from
  the user's perspective?" Combines the optimistic publishInitiated
  flag (covers the window between click and the first poll picking
  up the new history entry) with the canonical publishStatus signal,
  excluding cancelled/errored terminal states. Mirrors the condition
  PublishManager uses to decide whether to render the publishing
  banner so siblings (PublishHistory) can stay in sync. Soft errors
  count as "no longer in progress" -- they're reported by the saga
  or the watchdog when the publish has effectively ended.
  */
  getIsPublishing() {
    if (this.state.softErrorMessage) return false;
    if (this.state.publishInitiated) return true;
    return (
      this.isPublishingInProgress() &&
      !this.isLastPublishCancelled() &&
      !this.isLastPublishError()
    );
  }

  /*
  Notify parent (KioskManager) whenever getIsPublishing() flips. The
  comparison against _lastReportedIsPublishing avoids redundant
  callback invocations on every render.
  */
  reportPublishingStateChange() {
    if (typeof this.props.onPublishingStateChange !== "function") return;
    const isPublishing = this.getIsPublishing();
    if (this._lastReportedIsPublishing === isPublishing) return;
    this._lastReportedIsPublishing = isPublishing;
    this.props.onPublishingStateChange(isPublishing);
  }

  /*
  Returns the first entry in publishHistory whose publishId is not
  in the click-time snapshot, or null if no snapshot exists or no
  new entry has appeared yet. Iterates from the end since the new
  entry is the most recent push.
  */
  getNewPublishEntry() {
    const { knownPublishIdsAtClick } = this.state;
    if (!knownPublishIdsAtClick) return null;
    const history = this.props.kioskData?.publishHistory;
    if (!history || history.length === 0) return null;
    for (let i = history.length - 1; i >= 0; i--) {
      const entry = history[i];
      if (entry?.publishId && !knownPublishIdsAtClick.has(entry.publishId)) {
        return entry;
      }
    }
    return null;
  }

  clearWatchdog() {
    if (this._watchdogTimeout) {
      clearTimeout(this._watchdogTimeout);
      this._watchdogTimeout = null;
    }
  }

  /*
  Common transition for "publish failed before producing a history
  entry": clear the in-flight state, drop the snapshot and watchdog,
  and surface the message in a soft-error banner. Invoked from both
  the watchdog (timeout fired with no new entry) and the saga-error
  detector (KIOSK_PUBLISH_ERROR observed via kioskData).
  */
  handleSoftFailure(message) {
    this.clearWatchdog();
    this.setState({
      publishInitiated: false,
      knownPublishIdsAtClick: null,
      softErrorMessage: message
    });
  }

  isPublishingInProgressFromData(kioskData) {
    return !!(
      kioskData &&
      kioskData.publishStatus &&
      kioskData.publishStatus !== "complete"
    );
  }

  isPublishingInProgress() {
    return this.isPublishingInProgressFromData(this.props.kioskData);
  }

  isLastPublishCancelled() {
    const { kioskData } = this.props;
    if (!kioskData || !kioskData.publishHistory || kioskData.publishHistory.length === 0) {
      return false;
    }
    const lastEntry = kioskData.publishHistory[kioskData.publishHistory.length - 1];
    return !!(lastEntry && lastEntry.metadata && lastEntry.metadata.cancelledAt);
  }

  isLastPublishError() {
    const { kioskData } = this.props;
    if (!kioskData || !kioskData.publishHistory || kioskData.publishHistory.length === 0) {
      return false;
    }
    const lastEntry = kioskData.publishHistory[kioskData.publishHistory.length - 1];
    return !!(lastEntry && lastEntry.metadata && lastEntry.metadata.error);
  }

  handleDismissError() {
    this.setState({ errorDismissed: true });
  }

  handleDismissSoftError() {
    this.setState({ softErrorMessage: null });
  }

  /*
  Banner's View Details click. Opens the publish-status dialog for
  the most relevant publishId for the current banner state:
    - publishing: the new publish's entry (already gated -- the
      banner only renders the link when this entry exists)
    - error/complete: the most recent entry in publishHistory
  Centralizing this here lets KioskManager expose a single
  publishId-taking handler shared with PublishHistory rows.
  */
  handleBannerViewDetails() {
    const newEntry = this.getNewPublishEntry();
    const history = this.props.kioskData?.publishHistory;
    const fallbackId =
      history && history.length > 0
        ? history[history.length - 1].publishId
        : null;
    const publishId = newEntry?.publishId || fallbackId;
    if (publishId && this.props.handleViewDetailsClick) {
      this.props.handleViewDetailsClick(publishId);
    }
  }

  startPublishPolling() {
    this.stopPublishPolling();
    /*
    No immediate fetch here. For the handlePublishClick caller, the
    server has not had time to write the first publishHistory entry
    yet, so an immediate fetch would just capture the pre-publish
    state (typically the previous publish's "complete") as the
    client's first observation. That stale observation then became
    the first half of the wasInProgress/isInProgress comparison in
    componentDidUpdate, which could silently mask the completion
    transition for fast publishes whose entire lifecycle fits
    inside one poll window (see the fast-publish race handler in
    componentDidUpdate). The componentDidMount and componentDidUpdate
    callers already get current kioskData via KioskManager's own
    fetch on mount, so waiting until the first setInterval tick
    adds at most PUBLISH_POLL_INTERVAL of staleness on those paths
    in exchange for removing the stale-seed problem here.
    */
    this._publishStatusInterval = setInterval(() => {
      this.props.requestKiosk();
    }, PUBLISH_POLL_INTERVAL);
  }

  stopPublishPolling() {
    if (this._publishStatusInterval) {
      clearInterval(this._publishStatusInterval);
      this._publishStatusInterval = null;
    }
  }

  showCompleteBanner() {
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
    }
    this.setState({ publishCompletedAt: Date.now(), completeBannerDismissed: false });
    this._completeBannerTimeout = setTimeout(() => {
      this.setState({ publishCompletedAt: null });
      this._completeBannerTimeout = null;
    }, COMPLETE_BANNER_DURATION);
  }

  handleCloseBanner() {
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
      this._completeBannerTimeout = null;
    }
    this.setState({ publishCompletedAt: null, completeBannerDismissed: true });
  }

  checkForRecentCompletion() {
    const { kioskData } = this.props;
    if (!kioskData || !kioskData.publishHistory || kioskData.publishHistory.length === 0) {
      return;
    }
    if (this.isPublishingInProgress() || this.isLastPublishCancelled()) {
      return;
    }
    const history = kioskData.publishHistory;
    const lastCompleteEntry = [...history]
      .reverse()
      .find(entry => entry.status === "complete");
    if (!lastCompleteEntry) {
      return;
    }
    const completedAt = new Date(lastCompleteEntry.timestamp).getTime();
    const elapsed = Date.now() - completedAt;
    if (elapsed < COMPLETE_BANNER_DURATION) {
      const remaining = COMPLETE_BANNER_DURATION - elapsed;
      this.setState({ publishCompletedAt: completedAt, completeBannerDismissed: false });
      this._completeBannerTimeout = setTimeout(() => {
        this.setState({ publishCompletedAt: null });
        this._completeBannerTimeout = null;
      }, remaining);
    }
  }

  stripHtmlTags(html) {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }

  handleProjectChange(name, value) {
    if (!value || value === "") {
      return;
    }
    /*
    Update only local state -- do NOT persist the project change to
    the kiosk record yet. The dropdown is meant to (1) show what is
    currently published on the kiosk and (2) let the user pick what
    to publish next; the kiosk's stored projectID should only change
    when the user actually publishes. The selected project is sent
    along in the publish request body and persisted server-side as
    part of the publish flow (matching the revert flow's behavior).
    */
    this.props.handleSettingsInputChange("projectID", value);
  }

  handlePublishClick() {
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
      this._completeBannerTimeout = null;
    }
    this.clearWatchdog();

    /*
    Capture the set of publishIds present right now. The new publish
    will eventually push an entry whose publishId is not in this set;
    that's how the View Details gate (and the publishInitiated reset
    in componentDidUpdate) recognizes the new entry without relying
    on the previous-publish-may-be-stuck heuristics.
    */
    const history = this.props.kioskData?.publishHistory || [];
    const knownPublishIds = new Set(
      history.map(e => e?.publishId).filter(Boolean)
    );

    /*
    Seed lastSeenPublishErrorAt with the kiosk's current value so a
    pre-existing (already-acknowledged) error from a prior failed
    publish doesn't immediately re-trigger the soft-error banner.
    */
    const currentErrorAt = this.props.kioskData?.publishErrorAt || null;

    this.setState({
      publishInitiated: true,
      publishCompletedAt: null,
      completeBannerDismissed: false,
      errorDismissed: false,
      softErrorMessage: null,
      knownPublishIdsAtClick: knownPublishIds,
      lastSeenPublishErrorAt: currentErrorAt
    });

    this.props.publishKioskProject();
    this.startPublishPolling();

    this._watchdogTimeout = setTimeout(() => {
      this._watchdogTimeout = null;
      /*
      If we still have not observed a new entry by the watchdog
      deadline, the publish almost certainly failed before the API
      could record anything (silent network drop, timeout, etc.).
      The saga-error path covers most failures via publishErrorAt,
      but this catches the truly silent ones.
      */
      if (this.state.publishInitiated && !this.getNewPublishEntry()) {
        this.handleSoftFailure(
          "Publish did not start within the expected time. Please try again."
        );
      }
    }, PUBLISH_WATCHDOG_TIMEOUT);
  }

  render() {
    const { projects, kioskData } = this.props;
    const isOnline = kioskData?.isOnline;
    const publishingInProgress = this.isPublishingInProgress();
    const isLastPublishCancelled = this.isLastPublishCancelled();
    const isLastPublishError = this.isLastPublishError();
    const { softErrorMessage } = this.state;

    /*
    Soft (session-scoped) errors take priority over both the in-flight
    banner and any legacy publishHistory error -- they describe the
    most recent thing that happened in this session and have their own
    Try Again / Dismiss controls.
    */
    if (softErrorMessage) {
      return (
        <PublishStatusBanner
          status="error"
          onTryAgain={this.handlePublishClick}
          onDismiss={this.handleDismissSoftError}
        />
      );
    }

    if (this.state.publishInitiated || (publishingInProgress && !isLastPublishCancelled && !isLastPublishError)) {
      /*
      The View Details link is only meaningful once a publishHistory
      entry exists for the new publish. While publishInitiated is
      true and no new entry has shown up yet, gate the link with
      detailsReady=false so the banner shows "Acquiring details..."
      instead. Once a new entry exists (or we're observing a kiosk
      that's already mid-publish without a snapshot), enable the
      link normally.
      */
      const newEntry = this.getNewPublishEntry();
      const detailsReady = this.state.publishInitiated ? !!newEntry : true;
      return (
        <PublishStatusBanner
          status="publishing"
          detailsReady={detailsReady}
          onViewDetails={this.handleBannerViewDetails}
          onCancel={this.props.handlePublishCancelClick}
        />
      );
    }

    if (isLastPublishError && !this.state.errorDismissed) {
      return (
        <PublishStatusBanner
          status="error"
          onViewDetails={this.handleBannerViewDetails}
          onTryAgain={this.handlePublishClick}
          onDismiss={this.handleDismissError}
        />
      );
    }

    if (this.state.publishCompletedAt && !this.state.completeBannerDismissed) {
      return (
        <PublishStatusBanner
          status="complete"
          onViewDetails={this.handleBannerViewDetails}
          onClose={this.handleCloseBanner}
        />
      );
    }

    let projectList = [];
    if (projects) {
      if (Array.isArray(projects)) {
        projectList = projects;
      } else {
        projectList = Object.values(projects);
      }
    }

    const projectOptions = projectList
      .map(project => {
        const title = project.title?.en || project.title || "Untitled Project";
        return {
          value: project._id,
          label: this.stripHtmlTags(title)
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

    if (!kioskData?.projectID) {
      projectOptions.unshift({ value: "", label: "Select a project" });
    }

    const currentProjectID = kioskData?.projectID;
    const hasProjectSelected = !!currentProjectID;
    const isPublishDisabled = !hasProjectSelected || !isOnline || (publishingInProgress && !isLastPublishCancelled && !this.isLastPublishError());

    return (
      <div className="publish-manager" style={{ padding: "65px 75px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div className="project-select" style={{ minWidth: 200 }}>
          <Select
            value={currentProjectID || ""}
            options={projectOptions}
            onChange={this.handleProjectChange}
            name="projectID"
            hintText="Select a project"
            style={{ width: 200 }}
          />
        </div>
        <RaisedButton
          onClick={this.handlePublishClick}
          label="Publish"
          primary
          disabled={isPublishDisabled}
          className="publish-button"
        />
        {!hasProjectSelected && (
          <div className="publish-hint-no-project" style={{ color: "#666", fontSize: 14 }}>
            Select a project to publish to the kiosk.
          </div>
        )}
        {hasProjectSelected && !isOnline && (
          <div className="publish-hint-offline" style={{ color: "#666", fontSize: 14 }}>
            The kiosk computer needs to be online in order to publish.
          </div>
        )}
      </div>
    );
  }
}

PublishManager.propTypes = {
  projects: PropTypes.object,
  kioskData: PropTypes.object,
  handleSettingsInputChange: PropTypes.func.isRequired,
  publishKioskProject: PropTypes.func.isRequired,
  requestKiosk: PropTypes.func.isRequired,
  handlePublishCancelClick: PropTypes.func.isRequired,
  /*
  Invoked with a publishId when the user opens the publish-status
  dialog from the banner. PublishManager resolves the relevant
  publishId locally (new publish entry while publishing, last entry
  for error/complete) so the parent can use a single handler shape
  across the banner and the PublishHistory rows.
  */
  handleViewDetailsClick: PropTypes.func.isRequired,
  /*
  Optional callback invoked with a boolean whenever the derived
  "is a publish currently in flight?" value changes. KioskManager
  uses this to disable Restore actions in PublishHistory while a
  publish is active (concurrent publish/revert operations on the
  same kiosk are not safe on the API side).
  */
  onPublishingStateChange: PropTypes.func
};
